import { GetStaticProps } from 'next';
import Link from 'next/link';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

const ScienceEngagementStudy = () => {
  return (
    <LandingPageLayout
      title="Research Study: Science Class Engagement with Curipod | Curipod"
      subTitle="A 2024 study found that Curipod's gamified AI lessons boosted science engagement and understanding — with over 80% of students calling the lessons fun and 75% valuing the AI feedback."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>RESEARCH STUDY</HeroTag>
            <HeroTitle>Increasing Science Class Engagement with Curipod</HeroTitle>
            <HeroMeta>
              <MetaBadge>Middle School Science</MetaBadge>
              <MetaBadge>Gamification &amp; AI</MetaBadge>
              <MetaBadge>2024 Study</MetaBadge>
            </HeroMeta>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    82<StatUnit>%</StatUnit>
                  </StatNumber>
                  <StatLabel>Found Lessons Fun</StatLabel>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    65<StatUnit>%</StatUnit>
                  </StatNumber>
                  <StatLabel>Better Understanding</StatLabel>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    76<StatUnit>%</StatUnit>
                  </StatNumber>
                  <StatLabel>Valued AI Feedback</StatLabel>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* SUMMARY */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionText>
              A{' '}
              <a
                href="https://www.researchgate.net/publication/380890772_Gamificacao_e_Inteligencia_Artificial_na_educacao_estrategias_didaticas_nas_aulas_de_Ciencias_Naturais"
                target="_blank"
                rel="noopener noreferrer"
              >
                2024 study
              </a>{' '}
              found Curipod&apos;s gamified AI lessons boosted middle school science
              engagement and learning. Over 80% of students found the lessons fun, 65%
              reported better understanding, and 75% valued the AI feedback.
            </SectionText>
            <SectionText>
              The study shows that combining interactive technology with gamified
              strategies can make science more accessible and enjoyable&mdash;without
              sacrificing rigor. For school leaders, this study points to a clear
              opportunity: use Curipod to make core subjects more engaging while
              maintaining rigor.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* INTRODUCTION */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Introduction</SectionTitle>
            <SectionText>
              <a
                href="https://www.researchgate.net/publication/380890772_Gamificacao_e_Inteligencia_Artificial_na_educacao_estrategias_didaticas_nas_aulas_de_Ciencias_Naturais"
                target="_blank"
                rel="noopener noreferrer"
              >
                A recent study
              </a>{' '}
              from Portugal explored how gamified learning strategies combined with AI can
              transform middle school science classes. The researchers used Curipod
              &ndash; a platform combining interactive, game-like elements with AI support
              &ndash; during two classroom interventions for 8th and 9th graders. Students
              reported higher motivation, found the lessons more effective, and were eager
              to use similar methods again. For school leaders, this study shows how
              blending play and intelligent tech can make core subjects both more engaging
              and more effective.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* STUDENTS FIND LEARNING FUN AND EFFECTIVE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Students Find Learning Both Fun and Effective</SectionTitle>
            <SectionText>
              One of the standout findings is that{' '}
              <strong>students not only enjoyed the gamified AI lessons</strong> but also
              felt like they learned better because of them. In the study, the classes
              were designed with game elements like storylines and point scoring in
              Curipod. Nearly 65% of students rated the AI-enhanced lesson positively, and
              82% described Curipod as &lsquo;interesting and fun.&rsquo; For middle
              schoolers, that&apos;s a big deal &mdash; four out of five teens calling a
              science lesson fun is rare.
            </SectionText>
            <SectionText>
              Streamline the transition to learning impact: &ldquo;Importantly, the fun
              didn&apos;t come at the expense of learning; students overwhelmingly felt
              the approach helped them understand the content better.&rdquo; Particularly
              in the 9th grade group, many noted that learning through game-like
              challenges and AI feedback was easier and more engaging than traditional
              worksheets or slide lectures. They even said they&apos;d like to learn other
              topics in the same way. This indicates a sustained interest &ndash; not only
              did they enjoy <em>this</em> unit, but it whetted their appetite for more.
              It means that approaches like these can potentially reduce student boredom
              and increase time-on-task. A captivated student is likely a student who is
              absorbing the material. When students say they prefer these methods and find
              them more effective, it signals a pedagogical approach worth scaling.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* AI MAKES THE DIFFERENCE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>AI Makes the Difference in Engagement</SectionTitle>
            <SectionText>
              Gamification can boost motivation, but pairing it with AI created a more
              personalized and responsive experience. Curipod&apos;s AI evaluated student
              responses and suggested improvements in real time. Student feedback was
              positive: about 76% of students liked receiving AI feedback on their work.
              This kind of immediate, tailored feedback is something a single teacher in a
              large class might struggle to provide in real-time. But with AI, each
              student can get hints or corrections on the spot. Such support likely
              prevented frustration and kept students progressing through the lesson game.
            </SectionText>
            <SectionText>
              Moreover, Curipod&apos;s interactive design &ndash; quizzes, polls,
              competitive mini-games &ndash; ensured that every student had a role. The
              study noted that the platform&apos;s features were simple to use: nearly
              two-thirds of students found Curipod easy to navigate and access. This is
              important; no matter how powerful a tech tool is, if students (or teachers)
              can&apos;t figure it out, it flops. But here, even 13-14-year-olds had
              little trouble, indicating Curipod&apos;s UI is intuitive. This suggests a
              relatively low barrier to adoption in terms of user training.
            </SectionText>
            <SectionText>
              Student surveys indicate that narratives, points, and badges made lessons
              feel more like a game than a worksheet, boosting enjoyment and
              understanding. Essentially, AI and gamification transformed the classroom
              into a sort of educational game space &ndash; one where students were active
              players. They weren&apos;t just memorizing facts about, say, the water cycle
              or cell biology; they were likely completing challenges, getting instant
              feedback, and maybe even competing or collaborating with peers in real time.
              This interactive, game-like approach also taps into proven strategies like
              retrieval practice and immediate feedback, which are known to boost
              long-term retention &mdash; especially valuable for complex science topics.
              For a science curriculum, where understanding processes and systems is key,
              these techniques can make abstract concepts concrete. The study&apos;s
              abstract confirms this:{' '}
              <strong>
                students&apos; perceptions were positive and the didactic strategies were
                effective, as they &ldquo;helped them learn better&rdquo;
              </strong>{' '}
              despite minor internet issues.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* OVERCOMING BARRIERS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>
              Overcoming Barriers: Training Teachers and Supporting Innovation
            </SectionTitle>
            <SectionText>
              Many teachers are still cautious about AI, unsure of its role in the
              classroom or concerned about its complexity. Teachers might fear that AI
              will replace traditional teaching or worry about not understanding the
              technology. Leaders can ease these worries by presenting AI as a supportive
              tool that manages repetitive tasks and provides quick feedback, freeing
              teachers to focus on mentoring and deeper instruction. Starting with
              enthusiastic early adopters can build momentum, as their positive results
              inspire colleagues to try it themselves. Sharing quick success stories
              &mdash; like a spike in engagement after a single Curipod lesson &mdash; can
              motivate others to experiment.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* BRINGING IT TO YOUR SCHOOL */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>
              Bringing Thoughtful Gamified AI Learning to Your School
            </SectionTitle>
            <SectionText>
              The study&apos;s results make a strong case for bringing gamified AI
              learning into schools. For curriculum directors, a logical next step could
              be to identify subjects or grade levels that might benefit most from a
              motivational boost, making abstract content more accessible. Schools could
              pilot this approach by replacing a single unit&apos;s lectures or worksheets
              with Curipod&apos;s interactive lessons. Track engagement, formative scores,
              and feedback from both students and teachers to gauge impact. If results
              echo the study &mdash; higher enjoyment and stronger learning &mdash;
              leaders have a clear case to expand.
            </SectionText>
            <SectionText>
              This approach also aligns with broader shifts toward personalized and
              engaging learning. AI allows for some personalization (feedback tailored to
              each student&apos;s input), and gamification taps into intrinsic motivators.
              The study found these methods created &lsquo;an active and fun learning
              experience&rsquo; with measurable benefits to learning. Those are exactly
              the kind of outcomes schools strive for in the quest to improve achievement
              and student well-being.
            </SectionText>
            <SectionText>
              In summary, the marriage of gamification and AI via platforms like Curipod
              offers a{' '}
              <strong>powerful tool for principals and curriculum leaders</strong>. It
              revitalizes the curriculum, making learning exciting while maintaining
              rigor.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* SOURCE & CTA */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <CenteredSection>
            <SourceCard>
              <SourceLabel>SOURCE</SourceLabel>
              <SourceText>
                Moura, Adelina &amp; Henriques, Berta. (2024). Gamifica&ccedil;&atilde;o e
                Intelig&ecirc;ncia Artificial na educa&ccedil;&atilde;o:
                estrat&eacute;gias did&aacute;ticas nas aulas de Ci&ecirc;ncias Naturais.
              </SourceText>
              <a
                href="https://www.researchgate.net/publication/380890772_Gamificacao_e_Inteligencia_Artificial_na_educacao_estrategias_didaticas_nas_aulas_de_Ciencias_Naturais"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SourceLink>View on ResearchGate &rarr;</SourceLink>
              </a>
            </SourceCard>

            <GetStartedWrapper>
              <Link href="/signup" passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default ScienceEngagementStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: ${theme.colors.paleBeige};
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 52px;
  color: ${theme.colors.black};
  margin: 0 0 20px;
  line-height: 1.15;
  max-width: 800px;

  @media ${MOBILE} {
    font-size: 36px;
  }

  @media (max-width: 320px) {
    font-size: 28px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const StatBannerWrapper = styled.div`
  background: ${theme.colors.black};
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
  padding: 0 20px;

  @media ${MOBILE} {
    gap: 20px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 48px;

  @media ${MOBILE} {
    width: 100%;
    height: 1px;
    margin: 0;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 16px 24px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${({ $highlight }) => ($highlight ? theme.colors.orange : theme.colors.white)};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatUnit = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 26px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  a {
    color: ${theme.colors.orange};
    text-decoration: underline;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── SOURCE & CTA ──────────────────────────────────────────────────────────── */

const SourceCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 12px;
  padding: 24px 32px;
  box-shadow: ${theme.effects.defaultShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 20px;
  }
`;

const SourceLabel = styled.div`
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  margin-bottom: 8px;
`;

const SourceText = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin: 0 0 8px;
`;

const SourceLink = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orange};
  &:hover {
    text-decoration: underline;
  }
`;

const GetStartedWrapper = styled.div`
  margin-top: 8px;
`;
